import React from "react";
import "./OrderDetails.css";
import { FaEdit, FaTrash, FaSave, FaTimes } from "react-icons/fa";
import { ROLES } from "../constants/roles";
import { ORDER_STATUS , ORDER_STATUS_ARABIC } from "../constants/orderStatuses";
import OrderCategoriesField from "./OrderCategoriesField";
import OrderPicturesField from "./OrderPicturesField";

const OrderDetails = ({
  formData,
  isEditing,
  isValid,
  errors,
  loading,
  isSubmitting,
  onChange,
  onCategoriesChange,
  onPicturesAdd,
  onExistingPictureRemove,
  onNewPictureRemove,
  onSubmit,
  onEdit,
  onCancel,
  onDelete,
  shouldShowMeters
}) => {
  if (loading) {
    return <p style={{ textAlign: "center" }}>جاري تحميل البيانات...</p>;
  }
  // put json.parse in try catch to avoid crash if user data is corrupted
  let user = null;
  try {
    user = JSON.parse(localStorage.getItem("user"));
  } catch (e) {
    console.error("Error parsing user data:", e);
  }

  // 1. تعريف المراحل المسموحة لكل قسم لعدم تكرار الكود (DRY)
  const showroomStages = [
    ORDER_STATUS.INIT_MEASURE, 
    ORDER_STATUS.PLANNING, 
    ORDER_STATUS.FINAL_MEASURE
    // ملاحظة: إذا كان الشو روم هو من يقوم بتحويل الطلب إلى "تصنيع"، 
    // يجب عليك إضافة ORDER_STATUS.MANUFACTURING هنا لكي لا تُقفل القائمة في وجهه.
  ];

  const factoryStages = [
    ORDER_STATUS.MANUFACTURING, 
    ORDER_STATUS.INSTALLATION, 
    ORDER_STATUS.COMPLETED
  ];

  // 2. هل يحق للمستخدم فتح القائمة بناءً على الحالة الحالية للطلب؟
  const canAccessDropdown = (() => {
    if (!user) return false;
    if (user.role === ROLES.ADMIN) return true; // الآدمن يفتح كل شيء
    
    if (user.role === ROLES.SHOWROOM) {
      // إذا كانت الحالة الحالية للطلب ضمن صلاحيات الشو روم، افتح القائمة
      return showroomStages.includes(formData.status);
    }
    
    if (user.role === ROLES.FACTORY) {
      // إذا كانت الحالة الحالية ضمن صلاحيات المصنع، افتح القائمة
      return factoryStages.includes(formData.status);
    }
    
    return false;
  })();

  // 3. تعطيل الخيارات غير المسموحة داخل القائمة (إذا كانت القائمة مفتوحة أصلاً)
  const isOptionDisabled = (optionValue) => {
    if (user?.role === ROLES.ADMIN) return false;

    if (user?.role === ROLES.SHOWROOM) {
      return !showroomStages.includes(optionValue);
    }

    if (user?.role === ROLES.FACTORY) {
      // شرط المصنع: التركيب والمكتمل يحتاجون isValid
      if ((optionValue === ORDER_STATUS.INSTALLATION || optionValue === ORDER_STATUS.COMPLETED) && !isValid) {
        return true; 
      }
      return !factoryStages.includes(optionValue);
    }

    return true;
  };
  return (
    <div className={`edit-container ${isEditing ? "editing-mode" : "view-mode"}`}>
      <h2 className="edit-title">{isEditing ? "تعديل الطلب" : "تفاصيل الطلب"}</h2>

      <div className="order-metadata">
        <div>
          <p style={{ display: "inline-block",color: "#6c757d", fontSize: "14px" }}>رقم الطلب:</p>
          <strong style={{ marginLeft: "10px", fontSize: "16px" }}>
            {formData.order_no}
          </strong>
        </div>
        
        <div>
          <p style={{ display: "inline-block",color: "#6c757d", fontSize: "14px" }}>تاريخ الإضافة:</p>
          <strong style={{ marginLeft: "10px", fontSize: "16px" }}>
            {formData.createdAt}
          </strong>
        </div>
      </div>

      <form id="order-form" className="edit-form" onSubmit={onSubmit} noValidate>
        <div className="form-group full-width priority-center">
          <label>أولوية (مهم)</label>
          <label className="toggle">
            <input
              type="checkbox"
              name="priority"
              checked={!!formData.priority}
              onChange={onChange}
              disabled={!isEditing}
            />
            <span className="toggle-slider" />
            <span className="toggle-text">
              {formData.priority ? "مفعلة" : "غير مفعلة"}
            </span>
          </label>
        </div>
        <div className="form-group">
          <label>الاسم الكامل</label>
          <input
            type="text"
            name="name"
            value={formData.name}
            onChange={onChange}
            placeholder="ادخل الاسم هنا"
            disabled={!isEditing}
            className={!isEditing ? "input-readonly" : ""}
          />
          {isEditing && errors.name && <small className="error-text">{errors.name}</small>}
        </div>
        <div className="form-group">        
          <label>رقم الهاتف</label>
          <input
            type="text"
            name="phone"
            value={formData.phone}
            onChange={onChange}
            placeholder="09XXXXXXXX"
            disabled={!isEditing}
            className={!isEditing ? "input-readonly" : ""}
          />
          {isEditing && errors.phone && <small className="error-text">{errors.phone}</small>}
        </div>
        <div className="form-group">        
          <label>الموقع</label>
          <input
            type="text"
            name="location"
            value={formData.location}
            onChange={onChange}
            placeholder="ادخل الموقع هنا"
            disabled={!isEditing}
            className={!isEditing ? "input-readonly" : ""}
          />
          {isEditing && errors.location && <small className="error-text">{errors.location}</small>}
        </div>
        <OrderCategoriesField
          categories={formData.categories}
          error={errors.categories}
          disabled={!isEditing}
          onChange={onCategoriesChange}
          groupClassName="form-group full-width"
          showError={isEditing}
        />
        <div className="form-group">        
          <label>الحالة</label>
          {/* change logic here to use constants */}
          <select
            name="status"
            value={formData.status}
            onChange={onChange}
            disabled={!isEditing || !canAccessDropdown}
            className={!isEditing || !canAccessDropdown ? "input-readonly" : ""}
          >            {/* when you add a new option before "تصنيع" must to add it in "earlyStatus" in OrderDetailsPage.js */}
            <option value={ORDER_STATUS.INIT_MEASURE} disabled={isOptionDisabled(ORDER_STATUS.INIT_MEASURE)}>
              {ORDER_STATUS_ARABIC[ORDER_STATUS.INIT_MEASURE]}
            </option>
            
            <option value={ORDER_STATUS.PLANNING} disabled={isOptionDisabled(ORDER_STATUS.PLANNING)}>
              {ORDER_STATUS_ARABIC[ORDER_STATUS.PLANNING]}
            </option>
            
            <option value={ORDER_STATUS.FINAL_MEASURE} disabled={isOptionDisabled(ORDER_STATUS.FINAL_MEASURE)}>
              {ORDER_STATUS_ARABIC[ORDER_STATUS.FINAL_MEASURE]}
            </option> 
            
            <option value={ORDER_STATUS.MANUFACTURING} disabled={isOptionDisabled(ORDER_STATUS.MANUFACTURING)}>
              {ORDER_STATUS_ARABIC[ORDER_STATUS.MANUFACTURING]}
            </option>
            
            <option value={ORDER_STATUS.INSTALLATION} disabled={!isValid || isOptionDisabled(ORDER_STATUS.INSTALLATION)}>
              {ORDER_STATUS_ARABIC[ORDER_STATUS.INSTALLATION]}
            </option>
            
            <option value={ORDER_STATUS.COMPLETED} disabled={!isValid || isOptionDisabled(ORDER_STATUS.COMPLETED)}>
              {ORDER_STATUS_ARABIC[ORDER_STATUS.COMPLETED]}
            </option>
          </select>
        </div>
        {shouldShowMeters && (
          <>
            <div className="form-group">        
              <label>عدد الأمتار</label>
              <input
                type="number"
                name="meters"
                min="0"
                step="0.01"
                value={formData.meters || ""}
                onChange={onChange}
                placeholder="أدخل عدد الأمتار"
                disabled={!isEditing || formData.status !== ORDER_STATUS.MANUFACTURING}
                className={!isEditing ? "input-readonly" : ""}
                onKeyDown={(e) => {
                  if (["-", "e", "+"].includes(e.key)) {
                    e.preventDefault();
                  }
                }}
              />
              {isEditing && errors.meters && <small className="error-text">{errors.meters}</small>}
            </div>
          </>
        )}
        <div className="form-group" full-width>
          <label>الموقع على الخريطة</label>
          <input
            type="text"
            name="locationOnMap"
            value={formData.locationOnMap}
            onChange={onChange}
            placeholder="رابط الموقع على خرائط جوجل (اختياري)"
            disabled={!isEditing}
            className={!isEditing ? "input-readonly" : ""}
          />
          </div>
        <div className="form-group full-width">        
          <label>ملاحظات</label>
          <textarea
            name="notes"
            value={formData.notes}
            onChange={onChange}
            placeholder="تفاصيل إضافية (اختياري)"
            disabled={!isEditing}
            className={!isEditing ? "input-readonly" : ""}
          />
        </div>
        <OrderPicturesField
          pictures={formData.pictures}
          files={formData.pictureFiles}
          deletedPictureIds={formData.deletedPictureIds}
          error={errors.pictureFiles}
          disabled={!isEditing}
          onAddFiles={onPicturesAdd}
          onRemoveExisting={onExistingPictureRemove}
          onRemoveFile={onNewPictureRemove}
          groupClassName="form-group full-width"
          showError={isEditing}
        />
      </form>
      <div className="actions-row">
        {!isEditing ? (
          <>
            <button type="button" className="action-btn edit-btn" onClick={onEdit}>
              <FaEdit /> تعديل
            </button>
            <button type="button" className="action-btn delete-btn" onClick={onDelete}>
              <FaTrash /> حذف
            </button>
          </>
        ) : (
          <>
            {/* ربطنا الزر بالفورم عن طريق خاصية form="order-form" */}
            <button 
              type="submit" 
              form="order-form" 
              className="save-btn" 
              disabled={isSubmitting}
            >
              {isSubmitting ? <span className="loading"></span> : <><FaSave /> حفظ</>}
            </button>
            
            <button type="button" className="action-btn cancel-btn" onClick={onCancel}>
              <FaTimes /> إلغاء
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default OrderDetails;
